import { Form } from '@procurify/ui'

import {
  PRIMARY_ORG_CATEGORY_LABELS,
  SECONDARY_ORG_CATEGORY_LABELS,
} from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { GlobalCommandOrgCategoriesFormContent } from './components/GlobalCommandOrgCategoriesFormContent'
import { useGlobalCommandOrgCategoriesFormFormik } from './hooks/useGlobalCommandOrgCategoriesFormFormik'
import messages from './messages'
import { GlobalCommandOrgCategoriesFormStyled } from './styles/GlobalCommandOrgCategoriesFormStyled'
import {
  type GlobalCommandOrgCategoriesFormSchema,
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormValue,
} from './types'

// The agent titles the fields after whatever it is asking for, and for the org
// categories themselves it sends Procurify's own wording. Forwarding that would
// overwrite the names a tenant chose for its categories, so a title only counts
// as an override when it matches neither the stock name nor the tenant's.
const toLabelOverride = (
  label: string | undefined,
  orgLabels: string[]
): string | undefined => {
  if (!label) return undefined
  const normalized = label.trim().toLowerCase()
  return orgLabels.some(
    (orgLabel) => orgLabel.trim().toLowerCase() === normalized
  )
    ? undefined
    : label
}

export interface IGlobalCommandOrgCategoriesFormProps {
  primaryCategories: IGlobalCommandOrgCategoriesFormPrimaryCategory[]
  /**
   * Field titles supplied by the agent. They replace the org category titles
   * when the envelope asks for something else — a shipping location and its
   * PunchOut group, for instance.
   */
  primaryCategoryLabel?: string
  secondaryCategoryLabel?: string
  initialValue?: IGlobalCommandOrgCategoriesFormValue
  disabled?: boolean
  onSubmit: (value: IGlobalCommandOrgCategoriesFormValue) => void
  onCancel: () => void
}

export const GlobalCommandOrgCategoriesForm = ({
  primaryCategories,
  primaryCategoryLabel,
  secondaryCategoryLabel,
  initialValue,
  disabled,
  onSubmit,
  onCancel,
}: IGlobalCommandOrgCategoriesFormProps) => {
  const intl = useProcurifyIntl()

  const primaryLabelOverride = toLabelOverride(primaryCategoryLabel, [
    PRIMARY_ORG_CATEGORY_LABELS.SINGULAR,
    intl.formatMessage(messages.primaryCategoryLabel),
  ])
  const secondaryLabelOverride = toLabelOverride(secondaryCategoryLabel, [
    SECONDARY_ORG_CATEGORY_LABELS.SINGULAR,
    intl.formatMessage(messages.secondaryCategoryLabel),
  ])

  const { initialValues, validationSchema, handleSubmit } =
    useGlobalCommandOrgCategoriesFormFormik({
      primaryCategoryLabel: primaryLabelOverride,
      secondaryCategoryLabel: secondaryLabelOverride,
      initialValue,
      onSubmit,
    })

  return (
    <GlobalCommandOrgCategoriesFormStyled>
      <Form<GlobalCommandOrgCategoriesFormSchema>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <GlobalCommandOrgCategoriesFormContent
          primaryCategories={primaryCategories}
          primaryCategoryLabel={primaryLabelOverride}
          secondaryCategoryLabel={secondaryLabelOverride}
          disabled={disabled}
          onCancel={onCancel}
        />
      </Form>
    </GlobalCommandOrgCategoriesFormStyled>
  )
}
